import {AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, inject, input, OnDestroy, OnInit, ViewChild, ViewContainerRef, ViewRef} from '@angular/core';
import {TreeNode, TreeviewService} from '../../../../services/treeview.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'treeview-node', templateUrl: './treeview-node.component.html', styleUrls: ['./treeview-node.component.scss'],
})
export class TreeviewNodeComponent implements OnInit, OnDestroy, AfterViewInit {
    treeNode = input.required<TreeNode>();
    objectSelected = input.required<EventEmitter<string>>();
    treeNodeChildrenLoaded = input.required<EventEmitter<TreeNode[]>>();
    showStatusColorInTree = input.required<boolean>();

    @ViewChild('node', {static: false}) nodeElement: ElementRef<HTMLDivElement> | null = null;

    @ViewChild('childrenContainer', {read: ViewContainerRef, static: false}) childrenContainer: ViewContainerRef | undefined;
    public selected = false;
    private cachedChildren: Map<string, ViewRef> = new Map<string, ViewRef>();
    private destroyRef = inject(DestroyRef);

    constructor(private treeviewCacheService: TreeviewService) {
    }

    ngOnDestroy(): void {
        this.cachedChildren.forEach(c => c?.destroy());
        this.treeNode().expanded = false;
    }

    ngOnInit(): void {
        this.objectSelected().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
            if (id !== this.treeNode().objectID) {
                this.selected = false;
            } else {
                setTimeout(() => {
                    this.selected = true;
                    this.nodeElement?.nativeElement.scrollIntoView({behavior: 'instant', block: 'center'});
                }, 20);
            }
        });
    }

    ngAfterViewInit(): void {
        this.treeNode().element = this;
    }

    selectObject(): void {
        this.selected = true;
        this.objectSelected().emit(this.treeNode().objectID);
    }

    async switchExpandState(): Promise<void> {
        if (this.treeNode().expanded) {
            this.hideChildren();
        } else {
            await this.showChildren();
        }
    }

    async showChildren(): Promise<void> {
        this.treeNode().expanded = true;
        if (this.cachedChildren.size > 0) {
            this.cachedChildren.forEach(child => {
                if (child) {
                    this.childrenContainer?.insert(child);
                }
            });
        } else {
            await this.createChildNodes();
        }
    }

    removeChild(objectID): void {
        if (this.cachedChildren.size === 0 || !this.childrenContainer) {
            return;
        }
        const view = this.cachedChildren.get(objectID);
        if (view) {
            const i = this.childrenContainer.indexOf(view);
            this.childrenContainer.remove(i);
        }
        this.cachedChildren.delete(objectID);
    }

    addChild(treeNode: TreeNode): void {
        if (this.cachedChildren.size === 0 || !this.childrenContainer) {
            return;
        }
        const treeViewNode = this.childrenContainer.createComponent(TreeviewNodeComponent);
        treeViewNode.setInput('objectSelected', this.objectSelected());
        treeViewNode.setInput('treeNode', treeNode);
        this.cachedChildren.set(treeNode.objectID || '', treeViewNode.hostView);
    }

    hideChildren(): void {
        this.treeNode().expanded = false;
        while (this.childrenContainer && this.childrenContainer?.length > 0) {
            this.childrenContainer.detach(0);
        }
    }

    async createChildNodes(): Promise<void> {
        let childrenOfNode = this.treeNode().children || [];
        if (childrenOfNode.length === 0) {
            // Child Node Data is not preloaded or missing
            const id = this.treeNode().objectID;
            if (!id) {
                return;
            }
            childrenOfNode = (await this.treeviewCacheService.getSubObjects(id)).objects || [];
        }

        childrenOfNode.forEach(child => {
            if (!this.childrenContainer) {
                throw Error('Node is missing containerElement to put children into');
            }
            // Child Nodes were not preloaded are missing the parent and depth
            if (!child.parent && this.treeNode()) {
                this.treeNode().addChild(child);
                this.treeNodeChildrenLoaded().emit([child]);
            }
            const treeViewNode = this.childrenContainer?.createComponent(TreeviewNodeComponent);
            treeViewNode.setInput('objectSelected', this.objectSelected());
            treeViewNode.setInput('treeNode', child);
            treeViewNode.setInput('treeNodeChildrenLoaded', this.treeNodeChildrenLoaded());
            treeViewNode.setInput('showStatusColorInTree', this.showStatusColorInTree());
            this.cachedChildren.set(child.objectID || '', treeViewNode.hostView);
        });
    }

}
